import React, { useState, useEffect } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Checkbox, FormControlLabel } from '@material-ui/core';
import ActionButton from '../button/actionButton';
import TextField from '../input/textField';
import { client } from "../../utils/data/graphql/client";
import { completeOtcOrderMutation } from "../../utils/data/graphql/otc/mutations";
import { useAuth0 } from "@auth0/auth0-react";

const useStyles = makeStyles((theme) => ({
    root: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        width: '480px',
        minHeight: '420px',
        maxHeight: '90vh',
        borderRadius: '20px',
        background: theme.background.dialog,
        overflowY: 'auto',
    },
    cardContent: {
        margin: '32px !important',
        padding: '0 !important',
        display: 'flex',
        justifyContent: 'center',
    },
    backdrop: {
        ['@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none))']: {
            '-webkit-backdrop-filter': 'blur(2em)',
            backdropFilter: 'blur(2em)',
            backgroundColor: 'rgb(0 0 0 / 33%) !important',
        }
    },
    content: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '42px',
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(3),
    },
    heading: {
        marginTop: '12px',
        alignSelf: 'start'
    },
    confirmButton: {
        marginBottom: '40px',
        position: 'relative',
        marginTop: '24px',
    },
    infoContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginBottom: '16px'
    },
    infoInput: {
        margin: '12px 0px 12px 0px !important',
        '& .MuiInputBase-input.Mui-disabled': {
            color: '#ffffff',
            '-webkit-text-fill-color': '#ffffff',
        },
    },
    sectionLabel: {
        marginTop: '16px',
        marginBottom: '4px',
        alignSelf: 'start',
        fontSize: '12px',
        textTransform: 'uppercase',
        fontWeight: 600,
        color: theme.palette.text?.secondary || '#666',
    },
    errorText: {
        color: theme.palette.error?.main || '#f44336',
        fontSize: '12px',
        marginTop: '4px',
        marginLeft: '14px',
    },
    autoFilledBadge: {
        display: 'inline-block',
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        color: '#4caf50',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 600,
        marginLeft: '8px',
    },
    autoCalculateCheckbox: {
        marginTop: '8px',
        marginBottom: '8px',
        alignSelf: 'flex-start',
        '& .MuiTypography-body1': {
            fontSize: '13px',
            color: '#999',
        },
        '& .MuiCheckbox-root': {
            padding: '4px 9px',
        },
        '& .MuiCheckbox-root svg': {
            fontSize: '20px',
        },
        // Style for the checkmark path when checked
        '& .MuiCheckbox-root.Mui-checked svg path': {
            fill: '#ffffff',
        },
    },
}));

const CompleteOtcDepositDialog = ({ onClose, onComplete, orderId, depositInfo }) => {
    const classes = useStyles();
    const { user } = useAuth0();
    
    // Determine deposit type
    const depositType = depositInfo?.type?.toUpperCase() || 'FIAT';
    const depositCurrency = depositInfo?.currency?.toUpperCase() || '';
    const depositAmount = depositInfo?.amount || 0;
    
    // Check if it's a stablecoin deposit (1:1 with USD)
    const isStablecoin = depositType === 'STABLECOIN' || 
                         ['USDT', 'USDC', 'BUSD', 'DAI'].includes(depositCurrency);
    
    // Settlement details state
    const [payoutAmount, setPayoutAmount] = useState('');
    const [quoteRate, setQuoteRate] = useState('');
    // const [midmarketRate, setMidmarketRate] = useState('');
    // const [grossProfit, setGrossProfit] = useState('');
    // const [bps, setBps] = useState('');
    const [transactionFee, setTransactionFee] = useState('');
    const [transactionUrl, setTransactionUrl] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    
    // Track which field was last edited to prevent calculation loops
    const [lastEdited, setLastEdited] = useState(null);
    
    // Toggle for auto-calculation feature
    const [autoCalculate, setAutoCalculate] = useState(true);

    // Auto-fill payout amount for stablecoins (1:1 conversion)
    useEffect(() => {
        if (isStablecoin && depositAmount) {
            setPayoutAmount(depositAmount.toString());
        }
    }, [isStablecoin, depositAmount]);

    // Calculate payoutAmount when quoteRate changes (for fiat deposits)
    useEffect(() => {
        if (autoCalculate && !isStablecoin && lastEdited === 'quoteRate' && quoteRate && depositAmount && parseFloat(quoteRate) > 0) {
            const calculatedPayoutAmount = depositAmount / parseFloat(quoteRate);
            setPayoutAmount(calculatedPayoutAmount.toFixed(2));
        }
    }, [quoteRate, depositAmount, isStablecoin, lastEdited, autoCalculate]);

    // Calculate quoteRate when payoutAmount changes (for fiat deposits)
    useEffect(() => {
        if (autoCalculate && !isStablecoin && lastEdited === 'payoutAmount' && payoutAmount && depositAmount && parseFloat(payoutAmount) > 0) {
            const calculatedQuoteRate = depositAmount / parseFloat(payoutAmount);
            setQuoteRate(calculatedQuoteRate.toFixed(5));
        }
    }, [payoutAmount, depositAmount, isStablecoin, lastEdited, autoCalculate]);

    const formatCurrencyDisplay = (currency, amount) => {
        if (!currency || amount == null) return 'N/A';
        
        const upperCurrency = currency.toUpperCase();
        
        // Stablecoins should be formatted as "USDT 10.00" not with symbols
        const stablecoins = ['USDT', 'USDC', 'BUSD', 'DAI'];
        if (stablecoins.includes(upperCurrency)) {
            return `${upperCurrency} ${parseFloat(amount).toFixed(2)}`;
        }
        
        const currencySymbols = {
            'USD': '$',
            'AUD': 'A$',
            'EUR': '€',
            'GBP': '£',
            'JPY': '¥',
            'CAD': 'C$',
            'NZD': 'NZ$',
            'CHF': 'CHF ',
            'SGD': 'S$',
            'HKD': 'HK$',
            'MXN': 'MX$',
        };
        
        const symbol = currencySymbols[upperCurrency] || `${upperCurrency} `;
        return `${symbol}${parseFloat(amount).toFixed(2)}`;
    };

    const validateForm = () => {
        // Payout amount is required
        if (!payoutAmount || payoutAmount.trim() === '') {
            setError('USD payout amount is required');
            return false;
        }

        const amount = parseFloat(payoutAmount);
        if (isNaN(amount) || amount <= 0) {
            setError('USD payout amount must be a positive number');
            return false;
        }

        setError('');
        return true;
    };

    // Handler for payoutAmount change
    const handlePayoutAmountChange = (e) => {
        setPayoutAmount(e.target.value);
        setLastEdited('payoutAmount');
    };

    // Handler for quoteRate change
    const handleQuoteRateChange = (e) => {
        setQuoteRate(e.target.value);
        setLastEdited('quoteRate');
    };

    const onDone = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        try {
            const settlementDetails = {
                payoutAmount: payoutAmount.trim(),
                payoutCurrency: 'USD', // Always USD for v2
                settlementType: depositType,
            };

            if (isStablecoin) {
                // For stablecoin deposits
                if (transactionUrl.trim()) {
                    settlementDetails.transactionUrl = transactionUrl.trim();
                }
                
                // Add stablecoin-specific fields
                settlementDetails.stablecoinSymbol = depositCurrency;
                settlementDetails.quoteRate = '1.00000'; // 1:1 conversion
                settlementDetails.bps = '0'; // No spread on stablecoins
                
            } else {
                // For fiat deposits
                if (quoteRate.trim()) {
                    settlementDetails.quoteRate = quoteRate.trim();
                }

                // if (midmarketRate.trim()) {
                //     settlementDetails.midMarketRate = midmarketRate.trim();
                // }

                // if (grossProfit.trim()) {
                //     settlementDetails.grossProfit = grossProfit.trim();
                // }

                // if (bps.trim()) {
                //     settlementDetails.bps = bps.trim();
                // }

                if (transactionFee.trim()) {
                    settlementDetails.transactionFee = transactionFee.trim();
                }
            }

            // Common fields for both types
            if (notes.trim()) {
                settlementDetails.notes = notes.trim();
            }

            await client.mutate({
                mutation: completeOtcOrderMutation,
                variables: {
                    id: orderId,
                    input: {
                        createdBy: user?.email,
                        settlementDetails
                    }
                }
            });
            onClose && onClose();
            onComplete && onComplete(true);
        } catch (e) {
            console.error('Failed to complete deposit:', e);
            setError('Failed to complete deposit. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <Modal open={true} onClose={onClose} BackdropProps={{className:classes.backdrop}}>
            <Fade in={true}>
                <Card className={classes.root}>
                    <CardContent className={classes.cardContent}>
                        <ActionButton variant="secondary" size="lg"
                                      className={classes.closeButton}
                                      onClick={onClose}
                        >
                            Close
                        </ActionButton>
                        <div className={classes.content}>
                            <Typography variant="h3" className={classes.heading}>
                                Complete Deposit
                            </Typography>
                            
                            {/* Pending Transaction Details (Read-only) */}
                            {depositInfo && (
                                <>
                                    <Typography variant="body2" className={classes.sectionLabel}>
                                        Incoming Deposit
                                    </Typography>
                                    <div className={classes.infoContainer}>
                                        <TextField
                                            className={classes.infoInput}
                                            name='depositType'
                                            type='text'
                                            value={depositType}
                                            label="Settlement Type"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            disabled
                                        />
                                        <TextField
                                            className={classes.infoInput}
                                            name='depositCurrency'
                                            type='text'
                                            value={depositCurrency}
                                            label="Incoming Currency"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            disabled
                                        />
                                        <TextField
                                            className={classes.infoInput}
                                            name='depositAmount'
                                            type='text'
                                            value={formatCurrencyDisplay(depositCurrency, depositAmount)}
                                            label="Incoming Amount"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            disabled
                                        />
                                    </div>
                                </>
                            )}
                            
                            {/* USD Conversion Section */}
                            <Typography variant="body2" className={classes.sectionLabel}>
                            USDⓈ Conversion * (Required)
                            </Typography>
                            
                            {/* Auto-calculate toggle - only show for fiat deposits */}
                            {!isStablecoin && (
                                <FormControlLabel
                                    className={classes.autoCalculateCheckbox}
                                    control={
                                        <Checkbox
                                            checked={autoCalculate}
                                            onChange={(e) => setAutoCalculate(e.target.checked)}
                                            size="small"
                                            style={{
                                                color: autoCalculate ? '#4caf50' : 'rgba(255, 255, 255, 0.3)',
                                            }}
                                        />
                                    }
                                    label="Auto-calculate between Quote Rate and USDⓈ Amount"
                                />
                            )}
                            
                            <div className={classes.infoContainer}>
                                <Typography variant="caption" style={{ 
                                    marginBottom: '4px',
                                    fontSize: '11px',
                                    color: '#999'
                                }}>
                                    {isStablecoin 
                                        ? '1:1 conversion - Auto-filled but editable'
                                        : autoCalculate 
                                            ? 'The USDⓈ amount that will be credited to the customer\'s balance (auto-calculates with Quote Rate)'
                                            : 'Manual entry mode - enter amount independently'
                                    }
                                </Typography>
                                <TextField
                                    className={classes.infoInput}
                                    name='payoutAmount'
                                    type='number'
                                    value={payoutAmount}
                                    label={
                                        <span>
                                            USDⓈ Amount to Credit
                                            {isStablecoin && (
                                                <span className={classes.autoFilledBadge}>AUTO-FILLED</span>
                                            )}
                                        </span>
                                    }
                                    placeholder="Enter USD amount"
                                    InputLabelProps={{shrink: false, disableAnimation: true}}
                                    onChange={handlePayoutAmountChange}
                                    onWheel={(e) => e.target.blur()}
                                    required
                                    error={error.includes('payout')}
                                    inputProps={{
                                        step: '0.01',
                                        min: '0'
                                    }}
                                />
                            </div>

                            {/* Conditional sections based on deposit type */}
                            {isStablecoin ? (
                                // STABLECOIN-SPECIFIC FIELDS
                                <>
                                    <Typography variant="body2" className={classes.sectionLabel}>
                                        Blockchain Details (Optional)
                                    </Typography>
                                    <div className={classes.infoContainer}>
                                        <TextField
                                            className={classes.infoInput}
                                            name='transactionUrl'
                                            type='text'
                                            value={transactionUrl}
                                            label="Transaction URL"
                                            placeholder="Blockchain explorer link"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            onChange={(e) => setTransactionUrl(e.target.value)}
                                        />
                                        <Typography variant="caption" style={{ 
                                            marginTop: '-8px', 
                                            marginBottom: '8px',
                                            fontSize: '11px',
                                            color: '#999'
                                        }}>
                                            e.g., https://tronscan.org/#/transaction/...
                                        </Typography>
                                    </div>
                                </>
                            ) : (
                                // FIAT-SPECIFIC FIELDS
                                <>
                                    <Typography variant="body2" className={classes.sectionLabel}>
                                        Exchange Rate & Fees (Optional)
                                    </Typography>
                                    <div className={classes.infoContainer}>
                                        <Typography variant="caption" style={{ 
                                            marginBottom: '4px',
                                            fontSize: '11px',
                                            color: '#999'
                                        }}>
                                            {autoCalculate 
                                                ? 'Auto-calculates USDⓈ Amount when entered'
                                                : 'Manual entry mode - enter rate independently'
                                            }
                                        </Typography>
                                        <TextField
                                            className={classes.infoInput}
                                            name='quoteRate'
                                            type='number'
                                            value={quoteRate}
                                            label="Quote Rate"
                                            placeholder="e.g., 1.08696"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            onChange={handleQuoteRateChange}
                                            onWheel={(e) => e.target.blur()}
                                            inputProps={{
                                                step: '0.00001',
                                                min: '0'
                                            }}
                                        />
                                        
                                        {/* <TextField
                                            className={classes.infoInput}
                                            name='midmarketRate'
                                            type='number'
                                            value={midmarketRate}
                                            label="Midmarket Rate"
                                            placeholder="e.g., 1.08500"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            onChange={(e) => setMidmarketRate(e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            inputProps={{
                                                step: '0.00001',
                                                min: '0'
                                            }}
                                        /> */}
                                        
                                        {/* <TextField
                                            className={classes.infoInput}
                                            name='bps'
                                            type='number'
                                            value={bps}
                                            label="Basis Points (BPS)"
                                            placeholder="e.g., 50"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            onChange={(e) => setBps(e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            inputProps={{
                                                step: '1',
                                                min: '0'
                                            }}
                                        /> */}
                                        
                                        <TextField
                                            className={classes.infoInput}
                                            name='transactionFee'
                                            type='number'
                                            value={transactionFee}
                                            label="Transaction Fee (USD)"
                                            placeholder="e.g., 2.50"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            onChange={(e) => setTransactionFee(e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            inputProps={{
                                                step: '0.01',
                                                min: '0'
                                            }}
                                        />
                                        
                                        {/* <TextField
                                            className={classes.infoInput}
                                            name='grossProfit'
                                            type='number'
                                            value={grossProfit}
                                            label="Gross Profit(USD)"
                                            placeholder="e.g., 2.50"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            onChange={(e) => setGrossProfit(e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            inputProps={{
                                                step: '0.01',
                                                min: '0'
                                            }}
                                        /> */}
                                    </div>
                                </>
                            )}

                            {/* Notes Section - Common for both */}
                            <Typography variant="body2" className={classes.sectionLabel}>
                                Admin Notes (Optional)
                            </Typography>
                            <div className={classes.infoContainer}>
                                <TextField
                                    className={classes.infoInput}
                                    name='notes'
                                    type='text'
                                    value={notes}
                                    label="Notes"
                                    placeholder="Any additional information..."
                                    InputLabelProps={{shrink: false, disableAnimation: true}}
                                    onChange={(e) => setNotes(e.target.value)}
                                    multiline
                                    rows={3}
                                />
                            </div>

                            {error && (
                                <Typography className={classes.errorText}>
                                    {error}
                                </Typography>
                            )}
                            
                            <ActionButton
                                variant="primary"
                                size="lg"
                                className={classes.confirmButton}
                                onClick={onDone}
                                disabled={submitting}
                            >
                                {submitting ? 'Completing...' : 'Complete Deposit'}
                            </ActionButton>
                        </div>
                    </CardContent>
                </Card>
            </Fade>
        </Modal>
    )
}

export default CompleteOtcDepositDialog;


